import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppleAuth from './apple';
import { AppleSvg, FacebookSvg, GoogleSvg, MailSvg, VkSvg, YandexSvg } from '../../icons';
import { MainContext } from '../../context/main.context';
import Header from '../header';
import TelegramAuth from './telegram';
import { getAuthElemStyle, setUserAuthDataId } from '../../common/tools';
import Footer from '../footer';
import FacebookAuth from './facebook';
import LoginByMail from './email/login';
import Registration from './email/registration';
import { GoogleLoginPopup } from './google';
import AuthState from '../../hoc/AuthState';

type AUTH_PROVIDER = 'yandex'|'vkontakte'|'apple'|'google'|'facebook'|'google_adult';


export default function AuthMain() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [ settingAuth, setSettingsAuth ] = useState<any>(null);
    const { startCheckUserAuthState } = AuthState();
    const {
        settings,
        modalContent,
        session,
        source,
        locale,
        project,
        toggleLoader,
        setModalContent,
    } = useContext(MainContext);

    const _googleAuth = (method: string) => {
        const isPassportMethod = [ 'google', 'googleAdult' ].includes(method);
        const isPopupMethod = [ 'googlePopup', 'googlePopupAdult' ].includes(method);
        const provider: any = method === 'googleAdult' ? 'google_adult' : method;

        return (
            <div className="auth-modal-menu-body-button">
                <div className="auth-modal-menu-body-button-icon">
                    <GoogleSvg/>
                </div>
                <div
                    className="auth-modal-menu-body-button-title"
                    onClick={() => isPassportMethod ? _authClick(provider) : null}
                >
                    { isPopupMethod && <GoogleLoginPopup method={method}/> }
                    <div className="auth-modal-menu-body-button-title-value text-overflow">
                        Google
                    </div>
                </div>
            </div>
        );
    }

    const _appleAuth = () => {
        return (
            <div className="auth-modal-menu-body-button">
                <div className="auth-modal-menu-body-button-icon">
                    <AppleSvg/>
                </div>
                <div className="auth-modal-menu-body-button-title">
                    <AppleAuth/>
                    <div className="auth-modal-menu-body-button-title-value text-overflow">
                        Apple
                    </div>
                </div>
            </div>
        );
    }

    const _vkAuth = () => {
        return (
            <div className="auth-modal-menu-body-button">
                <div className="auth-modal-menu-body-button-icon">
                    <VkSvg/>
                </div>
                <div
                    className="auth-modal-menu-body-button-title"
                    onClick={() => _authClick('vkontakte')}
                >
                    <div className="auth-modal-menu-body-button-title-value text-overflow">
                        VK
                    </div>
                </div>
            </div>
        );
    }

    const _yandexAuth = () => {
        return (
            <div className="auth-modal-menu-body-button">
                <div className="auth-modal-menu-body-button-icon">
                    <YandexSvg/>
                </div>
                <div
                    className="auth-modal-menu-body-button-title"
                    onClick={() => _authClick('yandex')}
                >
                    <div className="auth-modal-menu-body-button-title-value text-overflow">
                        Yandex
                    </div>
                </div>
            </div>
        );
    };

    const _tgAuth = () => {
        return (
            <TelegramAuth/>
        );
    };

    const _mailAuth = () => {
        return (
            <div
                className="auth-modal-menu-body-button"
                onClick={() => {
                    if (modalContent) {
                        setModalContent({ component: <LoginByMail/> });
                        return;
                    }

                    _navigate('/session/login/email');
                }}
            >
                <div className="auth-modal-menu-body-button-icon">
                    <MailSvg/>
                </div>
                <div className="auth-modal-menu-body-button-title">
                    <div className="auth-modal-menu-body-button-title-value text-overflow">
                        {t('login.mail')}
                    </div>
                </div>
            </div>
        );
    };

    const _mailRegistration = () => {
        return (
            <div className="auth-modal-menu-footer">
                <div className="auth-modal-menu-footer-no-acc text-overflow">
                    {t('login.noAcc')}?
                </div>
                <div
                    className="auth-modal-menu-footer-reg text-overflow"
                    onClick={() => {
                        if (modalContent) {
                            setModalContent({ component: <Registration/> });
                            return;
                        }

                        _navigate('/session/registration');
                    }}
                >
                    {t('login.reg')}
                </div>
            </div>
        );
    }

    const _facebookAuth = () => {
        return (
            <div className="auth-modal-menu-body-button">
                <div className="auth-modal-menu-body-button-icon">
                    <FacebookSvg/>
                </div>
                <div className="auth-modal-menu-body-button-title">
                    <FacebookAuth/>
                    <div className="auth-modal-menu-body-button-title-value text-overflow">
                        Facebook
                    </div>
                </div>
            </div>
        );
    }

    const _formatAuthSetting = () => {
        if (!settings?.projects) {
            return (
                <div className="auth-modal-menu-body">
                    <div className="auth-modal-menu-body-buttons">
                        {_googleAuth('google')}
                        {_appleAuth()}
                        {_vkAuth()}
                        {_yandexAuth()}
                        {_tgAuth()}
                        {_facebookAuth()}
                        {_mailAuth()}
                    </div>
                    {_mailRegistration()}
                </div>
            );
        }

        const authList: any = [];
        let mailAuthExists: boolean = false;

        settings.projects.authMethods.forEach((auth: string) => {
            if (auth.includes('google')) {
                authList.push(_googleAuth(auth));
            } else if (auth === 'apple') {
                authList.push(_appleAuth());
            } else if (auth === 'vk') {
                authList.push(_vkAuth());
            } else if (auth === 'yandex') {
                authList.push(_yandexAuth());
            } else if (auth === 'tg') {
                authList.push(_tgAuth());
            } else if (auth === 'facebook') {
                authList.push(_facebookAuth());
            } else if (auth === 'mail') {
                authList.push(_mailAuth());
                mailAuthExists = true;
            }
        });

        return (
            <div className="auth-modal-menu-body">
                <div className="auth-modal-menu-body-buttons">
                    {authList}
                </div>
                {mailAuthExists ? _mailRegistration() : null}
            </div>
        );
    }

    useEffect(() => {
        if (settings) {
            setSettingsAuth(_formatAuthSetting());
        }
    }, [ settings ])

    // Метод для отключения лоадера, при возврате на данную страницу нажатием на кнопку "Назад" в браузере
    useEffect((): void => {
        window.addEventListener('pageshow', (event: PageTransitionEvent): void => {
            const perf = performance.getEntriesByType("navigation")[0];

            // @ts-ignore
            if (event.persisted || perf['type'] === 'back_forward') {
                toggleLoader(false);
            }
        });
    }, []);

    const _navigate = (path: string): void => {
        if (session) {
            navigate({
                pathname: path,
                search: `?session=${session}&source=${source}&locale=${locale}&project=${project}`,
            });
        } else {
            navigate(path);
        }
    }

    const _renderMenu = () => {
        return (
            <div className="auth-modal-menu">
                <div className="auth-modal-menu-title text-overflow">
                    {t('login.auth')}
                </div>
                {settingAuth}
            </div>
        );
    }

    const _authClick = (provider: AUTH_PROVIDER): void => {
        const userAuthData = {
            userAuthDataId: setUserAuthDataId(),
            session,
        }

        const state = encodeURIComponent(JSON.stringify(userAuthData));
        const url = `${process.env.REACT_APP_API_URL}/passport/${provider}?state=${state}`;

        startCheckUserAuthState(url);
    }

    return (
        <div className="auth" style={{background: !modalContent ? '#e8e8e8' : 'unset'}}>
            <div
                className="auth-container"
                style={{
                    background: !modalContent ? '#e8e8e8' : 'unset',
                    minHeight: !modalContent ? 'calc(100vh - 100px)' : '100%',
                }}
            >
                <div className="auth-modal" style={getAuthElemStyle(settings)}>
                    <Header origin="auth"/>
                    {settingAuth ? _renderMenu() : null}
                </div>
            </div>
            {!modalContent && <Footer/>}
        </div>
    );
}

AuthMain.displayName = 'Auth';